<template>
  <tr>
    <td v-if="showReadModels">
      <domain-model-read-model
        :system="system"
        :event="event"
        :refreshKey="refreshKey"
        :showEntities="showEntities"
      />
    </td>
    <td v-if="role && !role.isDisplayed" :rowspan="role.rowSpan">
      <domain-model-roles :system="system" :role="role" :refreshKey="refreshKey" />
    </td>
    <td>
      <domain-model-write-model
        :system="system"
        :event="event"
        :refreshKey="refreshKey"
        :showEntities="showEntities"
        :currentSystemEntitiesData="currentSystemEntitiesData"
        @refreshArrows="refreshArrows"
      />
    </td>
    <td v-if="event.index === 0" :rowspan="system.events.length">
      <domain-model-aggregate-root
        :system="system"
        :refreshKey="refreshKey"
        :boundedContext="boundedContext"
        :showEntities="showEntities"
        :currentSystemEntitiesData="currentSystemEntitiesData"
        @refreshArrows="refreshArrows"
      />
    </td>
    <td>
      <domain-model-resulting-events :system="system" :event="event" :refreshKey="refreshKey" />
    </td>
  </tr>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import DomainModelRoles from '@/components/DomainModelRoles.vue'
import DomainModelReadModel from '@/components/DomainModelReadModel.vue'
import DomainModelWriteModel from '@/components/DomainModelWriteModel.vue'
import DomainModelAggregateRoot from '@/components/DomainModelAggregateRoot.vue'
import DomainModelResultingEvents from '@/components/DomainModelResultingEvents.vue'
import { cardHelpers } from '@/mixins/cardHelpers'

const {
  system,
  event,
  boundedContext,
  showEntities,
  showReadModels,
  refreshKey
} = defineProps({
  system: Object,
  event: Object,
  boundedContext: Object,
  refreshKey: Number,
  showEntities: Boolean,
  showReadModels: Boolean
})
const emit = defineEmits(['refreshArrows'])

const store = useStore()

const workflow = computed(() => store.state.workflow.workflow)
const role = computed(() => {
  const currentRole = system.roles[event.index]
  const rowSpan = system.roles.filter((item) => item.id === currentRole.id).length
  const isDisplayed = event.index !== 0 && currentRole.name === system.roles[event.index - 1].name

  return { ...currentRole, index: event.index, rowSpan, isDisplayed }
})

const currentSystemEntitiesData = computed(() => {
  const entities = []
  const dataModels = workflow.value.dataModels || []
  const rootEntityNames = []

  for (const dataModel of dataModels) {
    for (const event of system.events) {
      const entityCard = cardHelpers.methods.getEntityCard(event)
      const writeModel = event.dataModels?.find((model) => model.type === 'Event Story')
      // Explanation of: field.dataFields?.length > 0
      // We only show entities that are truly part of the aggregate.
      // If an entity is referenced by ID only (i.e., no subfields in the command),
      // we treat it as external to the aggregate.
      // Therefore, related entities without any sub data fields in the command are excluded.
      const isRelatedEntity = writeModel?.dataFields?.some((field) => field.relatedEntityId === dataModel.id && field.dataFields?.length > 0)

      if (isRelatedEntity) {
        if (entityCard?.entityId === dataModel.id) {
          rootEntityNames.push(dataModel.name)
        }

        if (entities.every(entity => entity.name !== dataModel.name)) {
          entities.push(dataModel)
        }
      }
    }
  }

  const matchingRootEntity = system.groupedBy?.type === 'linked-entity'
    ? system.groupedBy.rootEntity
    : entities.find((entity) => entity.name === system.groupedBy?.text)

  return {
    entities: matchingRootEntity
      ? entities.filter(entity => !rootEntityNames.includes(entity.name))
      : entities.map(entity => rootEntityNames.includes(entity.name) ? { ...entity, isRoot: true } : entity),
    rootEntity: matchingRootEntity ? { ...matchingRootEntity, name: matchingRootEntity.name } : null
  }
})

const refreshArrows = () => {
  emit('refreshArrows')
}
</script>

<style scoped>
tr {
  td {
    padding: 10px 16px;
    position: static;

    &:focus-within, &:focus {
      outline: none;
    }
  }
}
</style>
